
import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

// Mock data for template types
const TEMPLATE_TYPES = [
  { id: 'course-completion', label: 'Course Completion' },
  { id: 'achievement', label: 'Achievement' },
  { id: 'participation', label: 'Participation' }
];

// Mock data for templates
const TEMPLATES = {
  'course-completion': [
    { id: 'cc-1', name: 'Standard Certificate', preview: '/images/templates/cert-1.png' },
    { id: 'cc-2', name: 'Premium Certificate', preview: '/images/templates/cert-2.png' },
    { id: 'cc-3', name: 'Executive Certificate', preview: '/images/templates/cert-3.png' },
  ],
  'achievement': [
    { id: 'ac-1', name: 'Gold Achievement', preview: '/images/templates/achievement-1.png' },
    { id: 'ac-2', name: 'Silver Achievement', preview: '/images/templates/achievement-2.png' },
  ],
  'participation': [
    { id: 'pa-1', name: 'Event Participation', preview: '/images/templates/participation-1.png' },
    { id: 'pa-2', name: 'Workshop Completion', preview: '/images/templates/participation-2.png' },
  ]
};

interface CertificateTemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedType: string;
  onSelectType: (type: string) => void;
  selectedTemplateId: string | null;
  onSelectTemplate: (id: string) => void;
}

const CertificateTemplateSelector = ({
  isOpen,
  onClose,
  onConfirm,
  selectedType,
  onSelectType,
  selectedTemplateId,
  onSelectTemplate
}: CertificateTemplateSelectorProps) => {
  // Get templates for the selected type
  const templates = TEMPLATES[selectedType as keyof typeof TEMPLATES] || [];
  
  const handleConfirm = () => {
    if (!selectedTemplateId) {
      alert("Please select a template");
      return;
    }
    onConfirm();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Select Certificate Template</DialogTitle>
        </DialogHeader>
        
        <Tabs value={selectedType} onValueChange={onSelectType} className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            {TEMPLATE_TYPES.map((type) => (
              <TabsTrigger key={type.id} value={type.id}>
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>
          
          <ScrollArea className="h-[350px] overflow-y-auto p-1">
            <div className="grid grid-cols-2 gap-4 py-2">
              {templates.map((template) => (
                <div 
                  key={template.id} 
                  onClick={() => onSelectTemplate(template.id)}
                  className={`
                    border rounded-md overflow-hidden cursor-pointer transition-all
                    ${selectedTemplateId === template.id 
                      ? 'border-primary shadow-md ring-2 ring-primary ring-opacity-50' 
                      : 'border-border hover:border-primary/50'}
                  `}
                >
                  <div className="aspect-[1.4/1] bg-muted w-full flex items-center justify-center">
                    {template.preview ? (
                      <img 
                        src={template.preview} 
                        alt={template.name}
                        className="object-contain h-full w-full"
                      />
                    ) : (
                      <div className="text-muted-foreground">No preview</div>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="font-medium text-sm">{template.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </Tabs>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!selectedTemplateId}
          >
            Use Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CertificateTemplateSelector;
